import { type ReactNode, useRef, useState } from "react";

import { useEvent } from "@/hooks";
import { type Media } from "@/types";
import { type UploadType, useUploadFile } from "@/user/services";

import DropZone, { type DropZoneRef } from "./DropZone";

type DropZoneServerProps = {
  type: UploadType;
  accept?: Array<"image/*" | "video/*" | "audio/*" | "application/*">;
  media?: {
    file_name: string;
    preview_url: string;
    size: number;
  } | null;
  label?: ReactNode;
  children?: ReactNode;
  onUpload?: (media: Media) => void;
  onDeleted?: () => void;
  hasBorder?: boolean;
  error?: boolean;
};

export default function DropZoneServer({
  type,
  media,
  label,
  accept,
  children,
  onUpload,
  onDeleted,
  hasBorder = true,
  error,
}: DropZoneServerProps) {
  const dropZoneRef = useRef<DropZoneRef>(null);

  const [progress, setProgress] = useState<number | undefined>(undefined);

  const { mutate: upload } = useUploadFile(type, setProgress);

  const onUploadEvent = useEvent(onUpload);

  const extension = media?.file_name?.split(".").pop();

  return (
    <DropZone
      ref={dropZoneRef}
      accept={accept}
      label={label}
      preview={media?.preview_url}
      previewFileName={media?.file_name}
      previewSize={media?.size}
      previewExtension={extension}
      progress={progress}
      hasBorder={hasBorder}
      onUpload={(file) => {
        upload(file, {
          onSuccess: (media) => {
            onUploadEvent(media);
          },
          onSettled: () => {
            setProgress(undefined);
            dropZoneRef.current?.reset();
          },
        });
      }}
      onDeleted={onDeleted}
      error={error}
    >
      {children}
    </DropZone>
  );
}
